import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const TEXTURE_IMG = "https://images.unsplash.com/photo-1619608155967-7a5058b2a963?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const chapters = [
    {
        num: "01",
        title: "The Standard",
        body: "Every piece begins with what it's made of — soft cotton yarn, polished resin, gleaming metals, quality fabrics. If a material doesn't feel beautiful in the hand, it never becomes part of the collection.",
        dark: false,
    },
    {
        num: "02",
        title: "The Hands",
        body: "Some pieces are made slowly at our own table — stitched, poured, polished. Others are discovered and hand-picked from makers we trust. Either way, nothing reaches you that we wouldn't keep ourselves.",
        dark: true,
    },
    {
        num: "03",
        title: "The Promise",
        body: "Made with creativity, chosen with care, and created to bring beauty, style, and comfort to your everyday life. — Kichazone",
        dark: false,
    },
];

const Chapter = ({ chapter, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={`grid grid-cols-1 gap-8 border-b border-line py-24 md:grid-cols-12 md:py-32 ${chapter.dark ? "bg-espresso text-sand" : "bg-sand text-espresso"}`}
        data-testid={`manifesto-chapter-${chapter.num}`}
    >
        <div className={`px-6 md:col-span-4 lg:px-10 ${index % 2 === 1 ? "md:order-2 md:col-start-9" : ""}`}>
            <span className={`font-mono text-6xl font-medium tracking-tight md:text-7xl ${chapter.dark ? "text-gold/40" : "text-espresso/20"}`}>
                {chapter.num}
            </span>
        </div>
        <div className={`px-6 md:col-span-6 lg:px-10 ${index % 2 === 1 ? "md:order-1 md:col-start-2" : "md:col-start-6"}`}>
            <h2 className="font-serif text-3xl font-light tracking-tight text-gold md:text-4xl">{chapter.title}</h2>
            <p className={`mt-6 max-w-lg text-base leading-relaxed ${chapter.dark ? "text-sand/85" : "text-clay"}`}>{chapter.body}</p>
        </div>
    </motion.div>
);

export const Manifesto = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

    return (
        <section id="craft" data-testid="manifesto-section">
            <div ref={ref} className="relative overflow-hidden border-b border-line">
                <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
                    <img src={TEXTURE_IMG} alt="" className="h-[130%] w-full object-cover" />
                </motion.div>
                <div className="absolute inset-0 bg-espresso/60" />
                <div className="relative mx-auto max-w-7xl px-6 py-32 lg:px-10 lg:py-44">
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="font-mono text-xs uppercase tracking-[0.25em] text-sand/70"
                    >
                        The Manifesto
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 max-w-3xl font-serif text-2xl font-light leading-snug tracking-tight text-sand md:text-3xl"
                    >
                        Discover a beautiful collection of <span className="italic text-gold">handmade creations,</span> fashionable accessories, and everyday essentials. From handmade purses, cozy blankets, resin coasters, jewelry boxes, and decorative bowls to stylish jewelry and carefully selected lip gloss, Kichazone offers something special for every style, space, and occasion.
                    </motion.h2>
                </div>
            </div>
            {chapters.map((c, i) => (
                <Chapter key={c.num} chapter={c} index={i} />
            ))}
        </section>
    );
};
